import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RequestTimeChangeOutcome, useUpdateRequestTime } from "@/components/RequestData/hooks/useUpdateRequestTime";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { InlineHint } from "@/ui/molecules/InlineHint";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import { ChangesSource, type OpenShiftRequest } from "@/utils/models";
import { isOpenShiftDurationAllowed, MAX_OPEN_SHIFT_DURATION_HOURS } from "@/utils/openShiftDuration";
import { requestShiftOptionsRole } from "@/utils/roles";
import { hasNurseResponse } from "@/utils/utilFunctions";
import { GapsSheetHoursPicker } from "./GapsSheetHoursPicker";
import { GapsPostedStep, widensAnsweredWindow } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest;
	/** `Nurse 7a-7p · Thu 1 Oct` — the shift being changed. */
	context: string;
	onStep: (step: GapsPostedStep) => void;
};

const SHEET = "gaps-posted-hours";

type Hours = { start: DateTime; end: DateTime };

/**
 * New hours for a posted shift (P3-10): the Requests screen's own time editor (`GapsSheetHoursPicker`, the staff sheet's
 * hours step's too) written through `useUpdateRequestTime` — its overlap-merge
 * prompt, its Nursa re-post warning and its rule that a request somebody has answered may only SHRINK.
 *
 * That rule is stated here up front, and a widened window is refused before the write rather than after it, where the
 * hook would only say so in an error. What the edit did is the hook's own outcome, resolved once its refresh is done —
 * a save, a merge, a cancelled prompt (logged as cancelled, not as an error) or a failure it has already shown.
 */
export const GapsPostedHoursStep: FC<Props> = ({ request, context, onStep }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { updateRequestTime } = useUpdateRequestTime();
	const [draft, setDraft] = useState<Hours>({ start: request.message.shift_start, end: request.message.shift_end });
	const [isSaving, setIsSaving] = useState<boolean>(false);
	// One save at a time: a second tap while the write (or its prompt) is out would edit the hours twice.
	const inFlightRef = useRef<boolean>(false);

	const isAnswered = hasNurseResponse(request);
	const original = { start: request.message.shift_start, end: request.message.shift_end };
	const isChanged = draft.start.toMillis() !== original.start.toMillis() || draft.end.toMillis() !== original.end.toMillis();
	// The hook's own rule, checked before the write: an answered request's window can only shrink.
	const isWidened = isAnswered && widensAnsweredWindow(original, draft);
	const isValid = isOpenShiftDurationAllowed(draft.start, draft.end) && !isWidened;

	const properties = useMemo(
		() => ({ department_id: chosenDepartment.department_id, request_id: request.id, is_answered: isAnswered }),
		[chosenDepartment.department_id, request.id, isAnswered],
	);

	const handleSave = useCallback(async (): Promise<void> => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		track("gaps_posted_hours_start", properties);
		setIsSaving(true);
		const outcome = await updateRequestTime(request, draft.start, draft.end, ChangesSource.Requests);
		setIsSaving(false);
		inFlightRef.current = false;
		if (outcome === RequestTimeChangeOutcome.Cancelled) {
			// The overlap merge prompt or the Nursa re-post warning was cancelled: the user chose to write nothing.
			track("gaps_posted_hours_cancelled", properties);
			return;
		}
		if (outcome === RequestTimeChangeOutcome.Failed) {
			// The hook has already shown why (its error snackbar); this records where from.
			trackError("gaps_posted_hours_error", { ...properties, outcome });
			return;
		}
		track("gaps_posted_hours_success", { ...properties, merged: outcome === RequestTimeChangeOutcome.Merged });
		onStep(GapsPostedStep.Actions);
	}, [draft, updateRequestTime, onStep, properties, request, track, trackError]);

	return (
		<SheetStep
			stepKey={GapsPostedStep.Hours}
			dataTestId={SHEET}
			title={t("gaps.postedSheet.hoursTitle")}
			subtitle={context}
			footer={
				<>
					<SheetStepButton tone="dismiss" onClick={() => onStep(GapsPostedStep.Actions)} disabled={isSaving} dataTestId={`${SHEET}-cancel`}>
						{t("back")}
					</SheetStepButton>
					<SheetStepButton
						tone="confirm"
						onClick={() => void handleSave()}
						disabled={!isChanged || !isValid}
						loading={isSaving}
						dataTestId={`${SHEET}-confirm`}
					>
						{t("gaps.postedSheet.hoursSave")}
					</SheetStepButton>
				</>
			}
		>
			{isAnswered && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone={isWidened ? "warning" : "info"} dataTestId={`${SHEET}-answered`}>
						{t("gaps.postedSheet.hoursAnswered")}
					</InlineHint>
				</Box>
			)}
			<GapsSheetHoursPicker
				startTime={request.message.shift_start}
				endTime={request.message.shift_end}
				onTimeChange={({ newStartTime, newEndTime }) => setDraft({ start: newStartTime, end: newEndTime })}
				// The classic card's own inputs: the request's first role (as the short role the options are keyed by) and its
				// job decide which shift lengths it offers.
				role={requestShiftOptionsRole(request.message.roles)}
				jobId={request.job_id}
				dataTestId={`${SHEET}-picker`}
				maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
			/>
		</SheetStep>
	);
};
